import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { Observable, throwError, forkJoin, of } from 'rxjs';
import { apiEndpoints } from 'src/app/api-endpoints';
import { toContextArray, toContextArrayOrEmpty, firstContextRecord } from 'src/app/common/bhub-common-module/utils/context-array.util';
import { catchError, switchMap, map } from 'rxjs/operators';
import {
  ViewCell,
  ViewTab,
  ViewManager,
  ViewRepeatingSection,
  PdfTabConfig,
  CalcOperand,
  InlineCalcOperand,
  ListCalcFilter,
  CellPosition
} from '../board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { DynamicTableService } from './dynamic-table.service';
import { CellFilterService } from '../common/bhub-common-module/services/cell-filter.service';

export interface PdfRepeatingSectionRequest {
  sectionId: string;
  y: number;
  rowHeight: number;
  rowBorder: boolean;
  alternateRowColor: string;
  // Template cells (the layout for one row)
  templateCells: PdfElement[];
  // Pre-resolved row data: each entry is a map of fieldName -> formatted value
  rows: { [fieldName: string]: any }[];
  // Per-row position/size overrides from PDF edit mode. Parallel to `rows`:
  // rowPositionOverrides[rowIndex] maps a template cell id (PdfElement.id) to the
  // overridden position to use for that cell in that row instead of the template's.
  // Undefined when no row was edited.
  rowPositionOverrides?: ({ [cellId: string]: { x: number; y: number; width: number; height: number } } | null)[];
  // Per-row cell deletions from PDF edit mode. Parallel to `rows`:
  // rowHiddenCells[rowIndex] lists template cell ids (PdfElement.id) to skip when
  // rendering that row. Undefined when no row had a cell deleted.
  rowHiddenCells?: (string[] | null)[];
}

export interface PdfGenerationRequest {
  viewId: number;
  tabId: string;
  pdfConfig: PdfTabConfig;
  canvasWidth: number;
  canvasHeight: number;
  elements: PdfElement[];
  repeatingSections?: PdfRepeatingSectionRequest[];
  // Keep-together groups: each is a set of element ids that must render on the same
  // page. The backend computes their bounding box and, if it would straddle a page
  // boundary, shifts the whole block (and everything below it) to the next page.
  keepTogetherGroups?: { id: string; cellIds: string[] }[];
  // Note: applicationId is automatically added by AppHttpInterceptor
}

export interface PdfElement {
  // The originating cell's id. Used to match per-row position overrides in
  // repeating sections; harmless/ignorable for top-level elements.
  id?: string;
  type: string;
  position: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  config: { [key: string]: any };
}

@Injectable({
  providedIn: 'root'
})
export class PdfGenerationService {
  private apiUrl = environment.apiConfig.baseUri + apiEndpoints.pdfGeneration;
  private pdfApis = {
    generate: '/generate',
  };

  constructor(
    private http: HttpClient,
    private dynamicTableService: DynamicTableService,
    private cellFilterService: CellFilterService
  ) {}

  /**
   * Generate a PDF from a view tab and context data.
   * Resolves any dataset-sourced repeating sections before building the request.
   */
  generatePdf(
    viewId: number,
    tab: ViewTab,
    contextData: { [key: string]: any },
    viewManager?: ViewManager | null,
    pdfOverrides?: Map<string, string>,
    pdfPositionOverrides?: Map<string, CellPosition>,
    pdfDeletedCellIds?: Set<string>
  ): Observable<Blob> {
    // Find repeating sections that need dataset fetching
    const datasetSections = (tab.repeatingSections || []).filter(
      s => s.dataSource === 'dataset' && s.datasetId
    );

    if (datasetSections.length === 0) {
      // No dataset fetching needed — build request synchronously
      const request = this.buildPdfRequest(viewId, tab, contextData, undefined, pdfOverrides, pdfPositionOverrides, pdfDeletedCellIds);
      return this.http.post(
        `${this.apiUrl}${this.pdfApis.generate}`,
        request,
        { responseType: 'blob' }
      ).pipe(catchError(this.handleError));
    }

    // Fetch dataset data for each repeating section
    const fetches$ = datasetSections.map(section => {
      const filters = this.cellFilterService.buildCellFilters(
        section.cellFilters,
        viewManager || null
      );
      return this.dynamicTableService.selectEntries(
        section.datasetId!, -1, undefined, filters, undefined, section.sortBy
      ).pipe(
        map(response => ({
          sectionId: section.id,
          data: response.rowData || []
        })),
        catchError(() => of({ sectionId: section.id, data: [] as any[] }))
      );
    });

    return forkJoin(fetches$).pipe(
      switchMap(results => {
        const fetchedData: { [sectionId: string]: any[] } = {};
        results.forEach(r => fetchedData[r.sectionId] = r.data);

        const request = this.buildPdfRequest(viewId, tab, contextData, fetchedData, pdfOverrides, pdfPositionOverrides, pdfDeletedCellIds);
        return this.http.post(
          `${this.apiUrl}${this.pdfApis.generate}`,
          request,
          { responseType: 'blob' }
        );
      }),
      catchError(this.handleError)
    );
  }

  /**
   * Build the PDF generation request from a view tab
   */
  private buildPdfRequest(
    viewId: number,
    tab: ViewTab,
    contextData: { [key: string]: any },
    fetchedData?: { [sectionId: string]: any[] },
    pdfOverrides?: Map<string, string>,
    pdfPositionOverrides?: Map<string, CellPosition>,
    pdfDeletedCellIds?: Set<string>
  ): PdfGenerationRequest {
    // Skip top-level cells deleted in PDF edit mode (instance id == cell id here).
    const elements: PdfElement[] = tab.cells
      .filter(cell => !pdfDeletedCellIds?.has(cell.id))
      .map(cell => this.convertCellToElement(cell, contextData));

    // Build repeating section elements
    const sections = (tab.repeatingSections || []).map(section =>
      this.buildRepeatingSectionElement(section, contextData, fetchedData, pdfOverrides, pdfPositionOverrides, pdfDeletedCellIds)
    );

    // Build keep-together groups from the tab's blocks. Drop any member that was
    // deleted in PDF edit mode, and skip empty groups.
    const keepTogetherGroups = (tab.keepTogetherBlocks || [])
      .map(block => ({
        id: block.id,
        cellIds: (block.memberCellIds || []).filter(id => !pdfDeletedCellIds?.has(id))
      }))
      .filter(group => group.cellIds.length > 0);

    return {
      viewId,
      tabId: tab.id,
      pdfConfig: tab.pdfConfig || {
        pageSize: 'A4',
        orientation: 'portrait',
        margins: { top: 40, right: 40, bottom: 40, left: 40 }
      },
      canvasWidth: tab.canvas.width,
      canvasHeight: tab.canvas.height,
      elements,
      repeatingSections: sections.length > 0 ? sections : undefined,
      keepTogetherGroups: keepTogetherGroups.length > 0 ? keepTogetherGroups : undefined
    };
  }

  /**
   * Convert a ViewCell to a PDF element for the backend
   */
  private convertCellToElement(
    cell: ViewCell,
    contextData: { [key: string]: any }
  ): PdfElement {
    const config: { [key: string]: any } = {};

    // Cells dragged over a repeating section in PDF edit mode carry their Y in
    // already-expanded (absolute) coordinates. Tell the backend to place them at
    // position.y directly, WITHOUT applying repeating-section expansion offset.
    if (cell.configuration?._pdfAbsoluteY) {
      config.absoluteY = true;
    }

    // If a manual override exists (from PDF edit mode), render as simple text
    if (cell.configuration.pdfOverrideValue !== undefined && cell.configuration.pdfOverrideValue !== null) {
      config.value = cell.configuration.pdfOverrideValue;
      config.fontSize = cell.configuration.pdfFontSize || 12;
      config.fontWeight = cell.configuration.pdfFontWeight || 'normal';
      config.fontStyle = cell.configuration.pdfFontStyle || 'normal';
      config.textAlign = cell.configuration.pdfTextAlign || 'left';
      config.color = cell.configuration.pdfColor || '#000000';

      return {
        id: cell.id,
        type: 'pdf_text',
        position: cell.position,
        config
      };
    }

    switch (cell.type) {
      case 'pdf_text':
        config.value = cell.configuration.pdfValue || '';
        config.fontSize = cell.configuration.pdfFontSize || 12;
        config.fontWeight = cell.configuration.pdfFontWeight || 'normal';
        config.fontStyle = cell.configuration.pdfFontStyle || 'normal';
        config.textAlign = cell.configuration.pdfTextAlign || 'left';
        config.color = cell.configuration.pdfColor || '#000000';
        break;

      case 'pdf_image':
        config.filePath = cell.configuration.pdfFilePath;
        config.width = cell.configuration.pdfImageWidth || cell.position.width;
        config.height = cell.configuration.pdfImageHeight || cell.position.height;
        // Resolve context image if needed (filePath from context)
        if (cell.configuration.pdfImageSource === 'context' && cell.dataBinding.contextVariable) {
          const contextObj = contextData[cell.dataBinding.contextVariable];
          if (contextObj && cell.dataBinding.fieldName) {
            config.filePath = contextObj[cell.dataBinding.fieldName];
          }
        }
        break;

      case 'pdf_field':
        config.label = cell.configuration.pdfFieldLabel;
        config.showLabel = cell.configuration.pdfShowLabel !== false;
        config.fontSize = cell.configuration.pdfFontSize || 12;
        config.format = cell.configuration.pdfFieldFormat;
        // Resolve the field value from context
        if (cell.dataBinding.contextVariable) {
          const contextObj = contextData[cell.dataBinding.contextVariable];
          if (contextObj && cell.dataBinding.fieldName) {
            config.value = contextObj[cell.dataBinding.fieldName];
          }
        }
        break;

      case 'pdf_table':
        config.columns = cell.configuration.pdfTableColumns || [];
        config.showHeader = cell.configuration.pdfShowHeader !== false;
        config.headerFontSize = cell.configuration.pdfHeaderFontSize || 12;
        config.headerFontWeight = cell.configuration.pdfHeaderFontWeight || 'bold';
        config.rowFontSize = cell.configuration.pdfRowFontSize || 10;
        config.data = [];
        // Resolve table data from context
        if (cell.dataBinding.contextVariable) {
          config.data = toContextArrayOrEmpty(contextData[cell.dataBinding.contextVariable]);
        }
        break;

      case 'pdf_page_break':
        // No additional config needed for page breaks
        break;

      case 'pdf_datetime':
        config.label = cell.configuration.pdfDateTimeLabel;
        config.showLabel = cell.configuration.pdfDateTimeShowLabel !== false;
        config.fontSize = cell.configuration.pdfDateTimeFontSize || 12;
        // Pre-format the date value
        const dateFormat = cell.configuration.pdfDateTimeFormat || 'MM/dd/yyyy';
        config.value = this.resolveDateTimeValue(cell.configuration.pdfDateTimeValue, dateFormat);
        break;

      case 'pdf_row':
        config.fontSize = cell.configuration.pdfRowContainerFontSize || 12;
        config.fontWeight = cell.configuration.pdfRowContainerFontWeight || 'normal';
        config.fontStyle = cell.configuration.pdfRowContainerFontStyle || 'normal';
        config.color = cell.configuration.pdfRowContainerColor || '#000000';
        config.align = cell.configuration.pdfRowContainerAlign || 'left';
        // Build concatenated value from all elements
        const rowElements = cell.configuration.pdfRowContainerElements || [];
        config.value = this.buildRowValue(rowElements, contextData);
        break;

      case 'calculation':
        config.label = cell.configuration.calcLabel || '';
        config.fontSize = cell.configuration.calcFontSize || 16;
        config.fontWeight = cell.configuration.calcFontWeight || 'normal';
        config.format = cell.configuration.calcResultFormat || 'number';
        config.decimalPlaces = cell.configuration.calcDecimalPlaces ?? 2;
        config.calcOperands = cell.configuration.calcOperands || [];
        // Pre-resolve the calculated value from context
        // (will be null for cells inside repeating sections that reference $sectionItem;
        //  the backend evaluates those per-row using config.sectionCalcOperands)
        config.value = this.evaluateCalcOperands(
          config.calcOperands,
          contextData
        );
        if (config.value !== null) {
          config.formattedValue = this.formatCalcValue(config.value, config.format, config.decimalPlaces);
        } else {
          config.formattedValue = '--';
        }
        break;

      case 'pdf_list_calc':
        config.label = cell.configuration.pdfListCalcLabel || '';
        config.showLabel = cell.configuration.pdfListCalcShowLabel !== false;
        config.fontSize = cell.configuration.pdfListCalcFontSize || 14;
        config.fontWeight = cell.configuration.pdfListCalcFontWeight || 'normal';
        config.format = cell.configuration.pdfListCalcFormat || 'number';
        config.decimalPlaces = cell.configuration.pdfListCalcDecimalPlaces ?? 2;
        // Pre-resolve the calculated value from context
        config.value = this.evaluateListCalc(cell.configuration, contextData);
        if (config.value !== null) {
          config.formattedValue = this.formatCalcValue(config.value, config.format, config.decimalPlaces);
        } else {
          config.formattedValue = '--';
        }
        break;

      case 'list_display':
        config.label = cell.configuration.listDisplayLabel;
        config.labelFontSize = cell.configuration.listDisplayLabelFontSize || 14;
        config.labelFontWeight = cell.configuration.listDisplayLabelFontWeight || 'bold';
        config.labelFontStyle = cell.configuration.listDisplayLabelFontStyle || 'normal';
        config.labelColor = cell.configuration.listDisplayLabelColor || '#000000';
        config.rowFontSize = cell.configuration.listDisplayRowFontSize || 12;
        config.rowFontWeight = cell.configuration.listDisplayRowFontWeight || 'normal';
        config.rowFontStyle = cell.configuration.listDisplayRowFontStyle || 'normal';
        config.rowColor = cell.configuration.listDisplayRowColor || '#000000';
        config.rowSpacing = cell.configuration.listDisplayRowSpacing || 4;
        config.emptyMessage = cell.configuration.listDisplayEmptyMessage || 'No items';
        // Build pre-rendered rows from context data
        const listElements = cell.configuration.listDisplayRowElements || [];
        if (cell.configuration.listDisplayContextVariable) {
          const contextObj = contextData[cell.configuration.listDisplayContextVariable];
          if (Array.isArray(contextObj) && contextObj.length > 0) {
            config.rows = contextObj.map(item => this.buildListRowValue(listElements, item));
          } else {
            config.rows = [];
          }
        } else {
          config.rows = [];
        }
        break;

      case 'pdf_checkbox':
        config.label = cell.configuration.pdfCheckboxLabel || '';
        config.boxSize = cell.configuration.pdfCheckboxBoxSize || 14;
        config.fontSize = cell.configuration.pdfFontSize || 12;
        config.fontWeight = cell.configuration.pdfFontWeight || 'normal';
        config.fontStyle = cell.configuration.pdfFontStyle || 'normal';
        config.color = cell.configuration.pdfColor || '#000000';
        config.boxColor = cell.configuration.pdfCheckboxBoxColor || '#000000';
        config.checkColor = cell.configuration.pdfCheckboxCheckColor || '#000000';
        config.borderThickness = cell.configuration.pdfCheckboxBorderThickness ?? 1;
        config.gap = cell.configuration.pdfCheckboxGap ?? 4;
        // Resolve checked from context if configured
        const cbCtxVar = cell.configuration.pdfCheckboxContextVariable;
        const cbFieldName = cell.configuration.pdfCheckboxFieldName;
        if (cbCtxVar && cbFieldName && contextData[cbCtxVar]) {
          const cbCtxObj = contextData[cbCtxVar];
          const cbValue = Array.isArray(cbCtxObj) && cbCtxObj.length > 0
            ? cbCtxObj[0][cbFieldName]
            : cbCtxObj[cbFieldName];
          config.checked = !!cbValue;
        } else {
          config.checked = cell.configuration.pdfCheckboxChecked || false;
        }
        break;

      case 'pdf_grid':
        config.columns = (cell.configuration.pdfGridColumns || []).map((col: any) => {
          if (col.width !== undefined) return { width: col.width };
          return { weight: col.weight || 1 };
        });
        config.rows = (cell.configuration.pdfGridRows || []).map((row: any) => {
          if (row.height !== undefined) return { height: row.height };
          return { weight: row.weight || 1 };
        });
        config.showBorders = cell.configuration.pdfGridShowBorders ?? true;
        config.borderColor = cell.configuration.pdfGridBorderColor || '#000000';
        config.borderThickness = cell.configuration.pdfGridBorderThickness ?? 1;
        if (cell.configuration.pdfGridBackgroundColor) {
          config.backgroundColor = cell.configuration.pdfGridBackgroundColor;
        }
        break;

      case 'pdf_line':
        config.orientation = cell.configuration.pdfLineOrientation || 'horizontal';
        config.style = cell.configuration.pdfLineStyle || 'solid';
        config.color = cell.configuration.pdfLineColor || '#000000';
        config.thickness = cell.configuration.pdfLineThickness ?? 1;
        break;

    }

    return {
      id: cell.id,
      type: cell.type,
      position: {
        x: cell.position.x,
        y: cell.position.y,
        width: cell.position.width,
        height: cell.position.height
      },
      config
    };
  }

  /**
   * Build a repeating section request element.
   * Resolves the data source and converts contained cells to template elements.
   * Each row's data is available to section cells via the '$sectionItem' context variable.
   */
  private buildRepeatingSectionElement(
    section: ViewRepeatingSection,
    contextData: { [key: string]: any },
    fetchedData?: { [sectionId: string]: any[] },
    pdfOverrides?: Map<string, string>,
    pdfPositionOverrides?: Map<string, CellPosition>,
    pdfDeletedCellIds?: Set<string>
  ): PdfRepeatingSectionRequest {
    // Resolve data rows
    let dataRows: any[] = [];
    if (section.dataSource === 'dataset' && fetchedData?.[section.id]) {
      dataRows = fetchedData[section.id];
    } else if (section.dataSource === 'context' && section.contextVariable) {
      const contextObj = contextData[section.contextVariable];
      if (Array.isArray(contextObj)) {
        dataRows = contextObj;
      }
    }

    // Pre-compute calculation cell values per row on the frontend.
    // For each calculation cell referencing $sectionItem, evaluate per-row
    // and store the result + formatted value directly on each row object.
    const calcCells = section.cells.filter(cell => {
      if (cell.type !== 'calculation') return false;
      const operands: CalcOperand[] = cell.configuration?.calcOperands || [];
      return operands.some(op => op.contextVariable === '$sectionItem');
    });

    if (calcCells.length > 0) {
      dataRows = dataRows.map(row => {
        const enrichedRow = { ...row };
        for (const cell of calcCells) {
          const rowContextData = { ...contextData, '$sectionItem': row };
          const value = this.evaluateCalcOperands(cell.configuration.calcOperands || [], rowContextData);
          const format = cell.configuration.calcResultFormat || 'number';
          const decimals = cell.configuration.calcDecimalPlaces ?? 2;
          // Store pre-computed values keyed by cell ID
          enrichedRow[`__calc_${cell.id}`] = value;
          enrichedRow[`__calcFmt_${cell.id}`] = value !== null
            ? this.formatCalcValue(value, format, decimals)
            : '--';
        }
        return enrichedRow;
      });
    }

    // Inject per-row overrides from PDF edit mode directly into row field values.
    // This overwrites the actual field the backend reads, so no backend changes needed.
    // Override keys are formatted as `${cellId}_row${rowIndex}`.
    if (pdfOverrides && pdfOverrides.size > 0) {
      dataRows = dataRows.map((row, rowIndex) => {
        let enrichedRow: any = null;
        for (const cell of section.cells) {
          const overrideKey = `${cell.id}_row${rowIndex}`;
          const overrideValue = pdfOverrides.get(overrideKey);
          if (overrideValue == null) continue;
          if (!enrichedRow) enrichedRow = { ...row };

          // Overwrite the field the backend will read for this cell
          if (cell.dataBinding?.contextVariable === '$sectionItem' && cell.dataBinding?.fieldName) {
            // Field-bound cell: overwrite the bound field
            enrichedRow[cell.dataBinding.fieldName] = overrideValue;
          }
          if (cell.type === 'calculation') {
            // Calculation cell: overwrite the pre-computed formatted value
            enrichedRow[`__calcFmt_${cell.id}`] = overrideValue;
            enrichedRow[`__calc_${cell.id}`] = parseFloat(overrideValue) || 0;
          }
          if (cell.type === 'pdf_row') {
            // Row container: overwrite all referenced section fields
            const rowElements = cell.configuration?.pdfRowContainerElements || [];
            rowElements
              .filter((elem: any) => elem.type === 'field' && elem.contextVariable === '$sectionItem')
              .forEach((elem: any) => {
                enrichedRow[elem.fieldName] = overrideValue;
              });
          }
        }
        return enrichedRow || row;
      });
    }

    // Per-row overrides for static/value cells (e.g. pdf_text, pdf_datetime).
    // These render from a single shared template `config.value`, so a per-row
    // edit can't be expressed by mutating the template. Instead route each such
    // cell's value through a synthetic row field the backend resolves via
    // sectionFieldName — the same per-row mechanism used for data-field cells.
    // Only cells edited in at least one row are converted; untouched cells stay
    // static. Every row is populated (edited rows get the edit, others get the
    // cell's normal text) so no row renders blank.
    const staticOverrideCellIds = new Set<string>();
    if (pdfOverrides && pdfOverrides.size > 0) {
      for (const cell of section.cells) {
        // Skip cells already handled by the field/calc/pdf_row branches above.
        if (cell.type === 'calculation' || cell.type === 'pdf_row') continue;
        if (cell.dataBinding?.contextVariable === '$sectionItem' && cell.dataBinding?.fieldName) continue;
        const hasOverride = dataRows.some(
          (_row, rowIndex) => pdfOverrides.get(`${cell.id}_row${rowIndex}`) != null
        );
        if (hasOverride) staticOverrideCellIds.add(cell.id);
      }

      if (staticOverrideCellIds.size > 0) {
        // The cell's normal (un-edited) rendered text, used to fill rows the
        // user didn't touch so they keep displaying their original value.
        const defaultText: { [cellId: string]: string } = {};
        for (const cell of section.cells) {
          if (!staticOverrideCellIds.has(cell.id)) continue;
          const value = this.convertCellToElement(cell, contextData).config?.value;
          defaultText[cell.id] = value != null ? String(value) : '';
        }

        dataRows = dataRows.map((row, rowIndex) => {
          let enrichedRow: any = null;
          for (const cellId of staticOverrideCellIds) {
            const override = pdfOverrides.get(`${cellId}_row${rowIndex}`);
            if (!enrichedRow) enrichedRow = { ...row };
            enrichedRow[`__pdfval_${cellId}`] = override != null ? override : defaultText[cellId];
          }
          return enrichedRow || row;
        });
      }
    }

    // Convert section cells to template elements.
    // For cells referencing '$sectionItem', we keep field metadata so the backend
    // can resolve values per row. For cells referencing other context, resolve normally.
    const templateCells = section.cells.map(cell => {
      const element = this.convertCellToElement(cell, contextData);

      // If this cell references $sectionItem, add field binding info
      // so the backend can resolve per-row values
      if (cell.dataBinding?.contextVariable === '$sectionItem' && cell.dataBinding?.fieldName) {
        element.config.sectionFieldName = cell.dataBinding.fieldName;
        element.config.sectionContextVariable = '$sectionItem';
      }

      // For pdf_row elements referencing $sectionItem, tag them too
      if (cell.type === 'pdf_row') {
        const rowElements = cell.configuration?.pdfRowContainerElements || [];
        const sectionFieldRefs = rowElements
          .filter((elem: any) => elem.type === 'field' && elem.contextVariable === '$sectionItem')
          .map((elem: any) => ({ fieldName: elem.fieldName, fieldFormat: elem.fieldFormat }));
        if (sectionFieldRefs.length > 0) {
          element.config.sectionFieldRefs = sectionFieldRefs;
        }
      }

      // For calculation cells referencing $sectionItem, tag with the pre-computed field names
      // so the backend reads the value from the row data (already computed above)
      if (cell.type === 'calculation') {
        const operands: CalcOperand[] = cell.configuration?.calcOperands || [];
        const hasSectionRefs = operands.some(op => op.contextVariable === '$sectionItem');
        if (hasSectionRefs) {
          element.config.sectionFieldName = `__calc_${cell.id}`;
          element.config.sectionFormattedFieldName = `__calcFmt_${cell.id}`;
          element.config.sectionContextVariable = '$sectionItem';
        }
      }

      // For static/value cells that were edited per-row (e.g. pdf_text), tag the
      // template so the backend reads each row's value from the synthetic field
      // populated above instead of the shared static config.value.
      if (staticOverrideCellIds.has(cell.id)) {
        element.config.sectionFieldName = `__pdfval_${cell.id}`;
        element.config.sectionContextVariable = '$sectionItem';
      }

      return element;
    });

    // section.height from the builder includes a 25px admin-only header bar.
    // Subtract it so the backend gets the actual row content height.
    const rowHeight = section.height - 25;

    // Per-row position/size overrides from PDF edit mode. For each data row,
    // collect any cells the user repositioned in that specific row, keyed by
    // template cell id (matches PdfElement.id on templateCells).
    let rowPositionOverrides: PdfRepeatingSectionRequest['rowPositionOverrides'];
    if (pdfPositionOverrides && pdfPositionOverrides.size > 0) {
      const built = dataRows.map((_row, rowIndex) => {
        let rowOv: { [cellId: string]: { x: number; y: number; width: number; height: number } } | null = null;
        for (const cell of section.cells) {
          const ov = pdfPositionOverrides.get(`${cell.id}_row${rowIndex}`);
          if (ov) {
            if (!rowOv) rowOv = {};
            rowOv[cell.id] = { x: ov.x, y: ov.y, width: ov.width, height: ov.height };
          }
        }
        return rowOv;
      });
      // Only include if at least one row actually has overrides.
      if (built.some(o => o !== null)) {
        rowPositionOverrides = built;
      }
    }

    // Per-row cell deletions: for each data row, list any cells removed in that
    // specific row so the backend skips them while keeping them in other rows.
    let rowHiddenCells: PdfRepeatingSectionRequest['rowHiddenCells'];
    if (pdfDeletedCellIds && pdfDeletedCellIds.size > 0) {
      const built = dataRows.map((_row, rowIndex) => {
        const hidden = section.cells
          .filter(cell => pdfDeletedCellIds.has(`${cell.id}_row${rowIndex}`))
          .map(cell => cell.id);
        return hidden.length > 0 ? hidden : null;
      });
      if (built.some(h => h !== null)) {
        rowHiddenCells = built;
      }
    }

    return {
      sectionId: section.id,
      y: section.y,
      rowHeight,
      rowBorder: section.rowBorder !== false,
      alternateRowColor: section.alternateRowColor || '',
      templateCells,
      rows: dataRows,
      rowPositionOverrides,
      rowHiddenCells
    };
  }

  /**
   * Build a concatenated string value from pdf_row elements
   */
  private buildRowValue(elements: any[], contextData: { [key: string]: any }): string {
    if (!elements || elements.length === 0) return '';

    return elements.map(elem => {
      switch (elem.type) {
        case 'text':
          return elem.textValue || '';

        case 'field':
          if (elem.contextVariable && elem.fieldName) {
            const contextObj = contextData[elem.contextVariable];
            if (contextObj) {
              const value = contextObj[elem.fieldName];
              return this.formatValue(value, elem.fieldFormat);
            }
          }
          return '';

        case 'datetime':
          return this.resolveDateTimeValue(elem.dateTimeValue, elem.dateTimeFormat);

        default:
          return '';
      }
    }).join('');
  }

  /**
   * Build a concatenated string value for a single list_display row
   */
  private buildListRowValue(elements: any[], item: any): string {
    if (!elements || elements.length === 0) return '';

    return elements.map(elem => {
      switch (elem.type) {
        case 'text':
          return elem.textValue || '';

        case 'field':
          if (elem.fieldName && item) {
            const value = item[elem.fieldName];
            return this.formatValue(value, elem.fieldFormat);
          }
          return '';

        default:
          return '';
      }
    }).join('');
  }

  /**
   * Format a value based on the specified format type
   */
  private formatValue(value: any, format: string | undefined): string {
    if (value === null || value === undefined) return '';

    if (!format) return String(value);

    switch (format) {
      case 'currency':
        const num = Number(value);
        if (isNaN(num)) return String(value);
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);

      case 'number':
        const numVal = Number(value);
        if (isNaN(numVal)) return String(value);
        return new Intl.NumberFormat('en-US').format(numVal);

      case 'percent':
        const pctVal = Number(value);
        if (isNaN(pctVal)) return String(value);
        return new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: 0 }).format(pctVal);

      case 'date':
        const date = new Date(value);
        if (isNaN(date.getTime())) return String(value);
        return date.toLocaleDateString('en-US');

      default:
        return String(value);
    }
  }

  /**
   * Resolve a datetime value configuration to a formatted string
   */
  private resolveDateTimeValue(dateTimeValue: any, format: string | undefined): string {
    if (!dateTimeValue) return '';

    let date: Date;

    if (dateTimeValue.mode === 'relative') {
      date = new Date();
      if (dateTimeValue.value === 'custom') {
        date.setDate(date.getDate() + (dateTimeValue.customDays || 0));
        date.setMonth(date.getMonth() + (dateTimeValue.customMonths || 0));
        date.setFullYear(date.getFullYear() + (dateTimeValue.customYears || 0));
      }
    } else if (dateTimeValue.mode === 'specific' && dateTimeValue.value) {
      date = new Date(dateTimeValue.value);
    } else {
      return '';
    }

    if (isNaN(date.getTime())) return '';

    // Simple date formatting based on common patterns
    const formatStr = format || 'MM/dd/yyyy';
    return this.formatDate(date, formatStr);
  }

  /**
   * Format a date using a format string
   */
  private formatDate(date: Date, format: string): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    const monthShort = monthNames[date.getMonth()].substring(0, 3);
    const monthFull = monthNames[date.getMonth()];

    return format
      .replace('MMMM', monthFull)
      .replace('MMM', monthShort)
      .replace('MM', month)
      .replace('dd', day)
      .replace('d', String(date.getDate()))
      .replace('yyyy', String(year))
      .replace('yy', String(year).substring(2));
  }

  /**
   * Evaluate a standard calculation cell's operands against context data
   */
  private evaluateCalcOperands(
    operands: CalcOperand[],
    contextData: { [key: string]: any }
  ): number | null {
    if (!operands || operands.length === 0) return null;

    let result: number | null = null;

    for (const operand of operands) {
      let numValue: number | null;

      if (operand.sourceType === 'value') {
        numValue = operand.literalValue ?? null;
      } else {
        const rawValue = this.getCalcOperandValue(operand, contextData);
        if (rawValue === null || rawValue === undefined) continue;
        numValue = this.parseNumericValue(rawValue);
      }

      if (numValue === null) continue;

      if (result === null) {
        result = operand.operator === '-' ? -numValue : numValue;
      } else {
        switch (operand.operator) {
          case '-': result -= numValue; break;
          case '*': result *= numValue; break;
          case '/': result = numValue !== 0 ? result / numValue : result; break;
          default:  result += numValue; break;
        }
      }
    }

    return result;
  }

  /**
   * Evaluate a PDF list calculation cell against context data
   */
  private evaluateListCalc(
    cellConfig: { [key: string]: any },
    contextData: { [key: string]: any }
  ): number | null {
    const contextVariable = cellConfig.pdfListCalcContextVariable || '';
    const fieldName = cellConfig.pdfListCalcFieldName || '';
    const operation = cellConfig.pdfListCalcOperation || 'sum';

    if (!contextVariable || !fieldName) return null;

    const ctxData = contextData[contextVariable];
    if (!ctxData) return null;

    const sourceItems = toContextArray(ctxData);
    if (!sourceItems) return null;
    let items: any[] = sourceItems;

    // Apply row filters before aggregation (mirrors PdfListCalcCellComponent.matchesFilters)
    const filters: ListCalcFilter[] = cellConfig.pdfListCalcFilters || [];
    if (filters.length > 0) {
      items = items.filter(item => this.matchesListCalcFilters(item, filters));
    }

    const inlineOperands: InlineCalcOperand[] = cellConfig.pdfListCalcInlineOperands || [];
    const values: number[] = [];

    for (const item of items) {
      const rawValue = item[fieldName];

      // COUNT: any non-empty value counts (mirrors PdfListCalcCellComponent)
      if (operation === 'count') {
        if (rawValue !== null && rawValue !== undefined && String(rawValue) !== '') {
          values.push(1);
        }
        continue;
      }

      if (rawValue === null || rawValue === undefined) continue;
      let rowValue = this.parseNumericValue(rawValue);
      if (rowValue === null) continue;

      if (inlineOperands.length > 0) {
        for (const op of inlineOperands) {
          let opValue: number | null;
          if (op.sourceType === 'value') {
            opValue = op.literalValue ?? null;
          } else {
            const raw = item[op.fieldName];
            opValue = raw !== null && raw !== undefined ? this.parseNumericValue(raw) : null;
          }
          if (opValue === null) continue;

          switch (op.operator) {
            case '-': rowValue -= opValue; break;
            case '*': rowValue *= opValue; break;
            case '/': rowValue = opValue !== 0 ? rowValue / opValue : rowValue; break;
            default:  rowValue += opValue; break;
          }
        }
      }

      values.push(rowValue);
    }

    let result: number | null = null;

    if (values.length > 0) {
      switch (operation) {
        case 'average': result = values.reduce((a, b) => a + b, 0) / values.length; break;
        case 'count':   result = values.length; break;
        case 'min':     result = Math.min(...values); break;
        case 'max':     result = Math.max(...values); break;
        case 'sum':
        default:        result = values.reduce((a, b) => a + b, 0); break;
      }
    }

    // Apply post-aggregation operands
    const postOperands: CalcOperand[] = cellConfig.pdfListCalcPostOperands || [];
    if (result !== null && postOperands.length > 0) {
      for (const operand of postOperands) {
        let numValue: number | null;
        if (operand.sourceType === 'value') {
          numValue = operand.literalValue ?? null;
        } else {
          const rawValue = this.getCalcOperandValue(operand, contextData);
          if (rawValue === null || rawValue === undefined) continue;
          numValue = this.parseNumericValue(rawValue);
        }
        if (numValue === null) continue;

        switch (operand.operator) {
          case '-': result -= numValue; break;
          case '*': result *= numValue; break;
          case '/': result = numValue !== 0 ? result / numValue : result; break;
          default:  result += numValue; break;
        }
      }
    }

    return result;
  }

  private matchesListCalcFilters(item: any, filters: ListCalcFilter[]): boolean {
    for (const filter of filters) {
      if (!filter.fieldName || !filter.value) continue;
      const fieldValue = item[filter.fieldName];
      if (fieldValue === null || fieldValue === undefined) return false;

      const fieldStr = String(fieldValue).toLowerCase();
      const filterStr = filter.value.toLowerCase();

      switch (filter.operator) {
        case 'equals':
          if (fieldStr !== filterStr) return false;
          break;
        case 'not_equals':
          if (fieldStr === filterStr) return false;
          break;
        case 'contains':
          if (!fieldStr.includes(filterStr)) return false;
          break;
        case 'greater_than': {
          const a = parseFloat(fieldStr), b = parseFloat(filterStr);
          if (isNaN(a) || isNaN(b) || a <= b) return false;
          break;
        }
        case 'less_than': {
          const a = parseFloat(fieldStr), b = parseFloat(filterStr);
          if (isNaN(a) || isNaN(b) || a >= b) return false;
          break;
        }
      }
    }
    return true;
  }

  /**
   * Get the value of a CalcOperand from context data
   */
  private getCalcOperandValue(operand: CalcOperand, contextData: { [key: string]: any }): any {
    if (!operand.contextVariable || !operand.fieldName) return null;

    const ctxData = contextData[operand.contextVariable];
    if (!ctxData) return null;

    const record = firstContextRecord(ctxData);
    return record ? record[operand.fieldName] : null;
  }

  /**
   * Parse a raw value to a number, stripping currency symbols and commas
   */
  private parseNumericValue(raw: any): number | null {
    if (typeof raw === 'number') return isNaN(raw) ? null : raw;
    if (typeof raw === 'string') {
      const cleaned = raw.replace(/[$€£¥,\s]/g, '');
      if (cleaned === '') return null;
      const num = Number(cleaned);
      return isNaN(num) ? null : num;
    }
    return null;
  }

  /**
   * Format a calculated value for PDF output
   */
  private formatCalcValue(value: number, format: string, decimalPlaces: number): string {
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', {
          style: 'currency', currency: 'USD',
          minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces
        }).format(value);
      case 'percent':
        return new Intl.NumberFormat('en-US', {
          style: 'percent',
          minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces
        }).format(value);
      default:
        return new Intl.NumberFormat('en-US', {
          minimumFractionDigits: decimalPlaces, maximumFractionDigits: decimalPlaces
        }).format(value);
    }
  }

  /**
   * Download a PDF blob as a file
   */
  downloadPdf(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Handle HTTP errors
   */
  private handleError(error: any): Observable<never> {
    console.error('PdfGenerationService error:', error);
    return throwError(() => new Error('Error generating PDF'));
  }
}
